import { Feedback, FeedbackMap } from './../../../enums/feedback.enums';
import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { loadMessages } from 'devextreme/localization';
import { Alert, Message, MessageEnteredEvent, User } from 'devextreme/ui/chat';
import { AiAssistantService } from '../../../services/ai-assistant.service';
import DataSource from 'devextreme/data/data_source';
import { Observable, Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth/services/auth.service';
import { AiPromptTemplateLibraryService } from 'src/app/core/services/ai-prompt-template-library.service';
import { addAiChatFeedbackAttempt } from 'src/app/core/store/actions/ai-assistant.actions';
import { Store } from '@ngrx/store';
import {
  getBatchOperationLocationIds,
  getLocatorLibraryId,
  getSelectedLocationId,
  getSelectedShapeIds
} from 'src/app/locator-store';
import { ChatService } from 'src/app/core/services/chat.service';

@Component({
  selector: 'atlas-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrl: './ai-chat.component.less',
  standalone: false
})
export class AiChatComponent implements OnDestroy, AfterViewInit, OnInit {
  private subscription = new Subscription();

  Feedback = Feedback;
  FeedbackMap = FeedbackMap;

  @Input() selectedFeedback: Feedback | null = null;

  dataSource: DataSource;

  @Input() user: User | null = null;

  typingUsers$: Observable<User[]>;

  alerts$: Observable<Alert[]>;

  currentPromptTemplateText$: Observable<string> =
    this.aiPromtTemplateLibraryService.currentTemplatePromptText$;

  copyButtonIcon: string;
  libraryId: number | null = null;
  selectedLocationIds: number[] | null = null;
  selectedLocationId: number | null = null;
  selectedShapeIds: number[] | null = null;

  constructor(
    private store$: Store,
    private authService: AuthService,
    public readonly aiAssistantService: AiAssistantService,
    private chatService: ChatService,
    private readonly aiPromtTemplateLibraryService: AiPromptTemplateLibraryService
  ) {
    loadMessages(this.aiAssistantService.getDictionary());

    this.dataSource =
      this.aiAssistantService.dataSource ?? new DataSource<any>([]);
    this.alerts$ = this.aiAssistantService.alerts$;
    this.typingUsers$ = this.aiAssistantService.typingUsers$;
    this.copyButtonIcon = 'copy';
    this.aiAssistantService.isDisabled = false;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.store$.select(getLocatorLibraryId).subscribe((libraryId) => {
        this.libraryId = libraryId;
      })
    );
    this.subscription.add(
      this.store$
        .select(getBatchOperationLocationIds)
        .subscribe((locationIds) => {
          this.selectedLocationIds = locationIds;
        })
    );
    this.subscription.add(
      this.store$.select(getSelectedLocationId).subscribe((locationId) => {
        this.selectedLocationId = locationId;
      })
    );
    this.subscription.add(
      this.store$.select(getSelectedShapeIds).subscribe((shapeIds) => {
        this.selectedShapeIds = shapeIds ?? null;
      })
    );
  }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.currentPromptTemplateText$.subscribe((promptTemplateText) => {
        const value = promptTemplateText ?? '';
        this.setTextAreaValue(value);

        if (value) {
          this.setTextAreaPlaceholder('');
        } else {
          this.setTextAreaPlaceholder(
            $localize`:@@askAiAssistantPlaceholder:Ask AI assistant...`
          );
        }
      })
    );

    this.aiAssistantService.connectWebSocket();
  }

  hasFeedback(data: any) {
    return this.aiAssistantService.feedbackDictionary.hasOwnProperty(
      data.message.id
    );
  }

  isToolMessage(message: Message): boolean {
    const text = message?.text ?? '';
    return text.includes('🔧') || text.includes('✅');
  }

  isToolExecuting(message: Message): boolean {
    return (message?.text ?? '').includes('🔧');
  }

  isToolCompleted(message: Message): boolean {
    return (message?.text ?? '').includes('✅');
  }

  getToolDisplayText(message: Message): string {
    const text = message?.text ?? '';
    return text.replace(/^\s*[🔧✅]\s*/, '').trim();
  }

  getToolIcon(message: Message): string {
    return this.isToolCompleted(message) ? 'check_circle' : 'build';
  }

  isFeedbackSelected(): boolean {
    return this.selectedFeedback !== null;
  }

  giveFeedback(message: Message, feedback: Feedback) {
    this.store$.dispatch(
      addAiChatFeedbackAttempt({
        messageId: message.id,
        feedback,
        conversationId: this.aiAssistantService.getCurrentConversationId()
      })
    );
  }

  convertToHtml(message: Message): string {
    return this.aiAssistantService.convertToHtml(message.text ?? '');
  }

  async onMessageEntered(e: MessageEnteredEvent) {
    if (!this.aiAssistantService.alerts.length) {
      this.aiAssistantService.toggleDisabledState(true, e.event);
    }

    try {
      if (this.libraryId) {
        const locationShapeSelection = this.buildLocationShapeSelection();

        await this.chatService
          .setUserSelectedState(this.libraryId, locationShapeSelection)
          .toPromise();
      }
      await this.aiAssistantService.onMessageEntered(e);
    } finally {
      this.setTextAreaPlaceholder(
        $localize`:@@askAiAssistantPlaceholder:Ask AI assistant...`
      );
    }
  }

  onCopyButtonClick(message: Message) {
    navigator.clipboard?.writeText(message.text ?? '');

    this.copyButtonIcon = 'check';

    setTimeout(() => {
      this.copyButtonIcon = 'copy';
    }, 2500);
  }

  async onRegenerateButtonClick() {
    this.aiAssistantService.removeLastMessage();
    this.aiAssistantService.toggleDisabledState(true);

    this.aiAssistantService.regenerate();
  }

  private buildLocationShapeSelection(): Array<{
    libraryDataId: number | null;
    shapeId: number | null;
  }> {
    const selectedLocation = this.selectedLocationId ?? null;

    if (selectedLocation !== null) {
      return this.buildSingleLocationSelection(selectedLocation);
    }

    const selectedLocations = this.selectedLocationIds ?? [];
    if (selectedLocations.length > 1) {
      return this.buildMultipleLocationsSelection(selectedLocations);
    }

    return [{ libraryDataId: null, shapeId: null }];
  }

  private buildSingleLocationSelection(selectedLocationId: number): Array<{
    libraryDataId: number | null;
    shapeId: number | null;
  }> {
    if (!this.selectedShapeIds?.length) {
      return [{ libraryDataId: selectedLocationId, shapeId: null }];
    }

    return this.selectedShapeIds.map((shapeId) => ({
      libraryDataId: selectedLocationId,
      shapeId: shapeId ?? null
    }));
  }

  private buildMultipleLocationsSelection(
    selectedLocationIds: number[]
  ): Array<{ libraryDataId: number | null; shapeId: number | null }> {
    return selectedLocationIds.map((locationId) => ({
      libraryDataId: locationId,
      shapeId: null
    }));
  }

  ngOnDestroy(): void {
    this.aiAssistantService.disconnectWebSocket();
    this.subscription.unsubscribe();
  }

  setTextAreaValue(value: string) {
    const textarea = document.querySelector(
      'dx-chat textarea'
    ) as HTMLTextAreaElement;

    if (textarea) {
      textarea.value = value;
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  setTextAreaPlaceholder(placeholder: string) {
    const placeholderDiv = document.querySelector(
      'dx-chat .dx-texteditor-input-container .dx-placeholder'
    ) as HTMLDivElement;
    if (placeholderDiv) {
      placeholderDiv.dataset.dx_placeholder = placeholder;
    }
  }
}
